import { Component, computed, inject, input } from "@angular/core";
import { ControlContainer, FormGroupDirective, ReactiveFormsModule } from "@angular/forms";
import { ApplicationType, IBaseT1Card, isJobsBillingDashboard, JobsBillingCard } from "@app/views-interfaces";
import { TranslateModule } from "@ngx-translate/core";
import { KENDO_TOOLTIPS } from "@progress/kendo-angular-tooltip";
import { CardModalService } from "@shared/card-modal";
import { DashboardsService, HelpersService, IFirmGroupsService } from "@shared/index";
import { CardPanelType, JobsJobAccessField } from "@shared/interfaces";
import { JobsAndBillingCommonService } from "@shared/jobs-and-billing-common-service/jobs-and-billing-common.service";
import { SharedModule } from "@shared/shared.module";
import { JumpstartComponentsModule } from "@wk/components-v3-angular17";

type GroupAccessFunction = (field?: JobsJobAccessField | null) => boolean;

@Component({
    selector: "wk-groups-methods-panel",
    imports: [KENDO_TOOLTIPS, JumpstartComponentsModule, SharedModule, TranslateModule, ReactiveFormsModule],
    templateUrl: "./groups-methods-panel.component.html",
    styleUrl: "./groups-methods-panel.component.scss",
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class GroupsMethodsPanelComponent {
    private readonly cardModalService = inject(CardModalService);
    private readonly dashboardsService = inject(DashboardsService);
    protected readonly helpersService = inject(HelpersService);
    private readonly iFirmGroupsService = inject(IFirmGroupsService);
    private readonly jobsAndBillingCommonService = inject(JobsAndBillingCommonService);
    protected readonly CardPanelType = CardPanelType;

    public readonly type = input<CardPanelType>();

    protected readonly isGroup1Enabled = computed<boolean>(() => {
        const type = this.type();
        switch (type) {
            case CardPanelType.Job:
                return this.iFirmGroupsService.isGroup1JobsEnabled();
            case CardPanelType.Contact:
                return this.iFirmGroupsService.isGroup1ContactsEnabled();
            default:
                return true;
        }
    });

    protected readonly isGroup2Enabled = computed<boolean>(() => {
        const type = this.type();
        switch (type) {
            case CardPanelType.Job:
                return this.iFirmGroupsService.isGroup2JobsEnabled();
            case CardPanelType.Contact:
                return this.iFirmGroupsService.isGroup2ContactsEnabled();
            default:
                return true;
        }
    });

    protected readonly getEditGroup1Field = computed<JobsJobAccessField | null>(() => {
        const type = this.type();
        if (type === CardPanelType.Job) {
            return "job_group_1";
        }

        return null;
    });

    protected readonly getEditGroup2Field = computed<JobsJobAccessField | null>(() => {
        const type = this.type();
        if (type === CardPanelType.Job) {
            return "job_group_2";
        }

        return null;
    });

    protected readonly getGroup1Field = computed<string>(() => {
        const dashboard = this.dashboardsService.currentDashboard();
        const type = this.type();
        if (!dashboard || !type) return "";

        if (isJobsBillingDashboard(dashboard)) {
            if (type === CardPanelType.Job) {
                return "jobGroup1";
            }

            if (type === CardPanelType.Contact) {
                return "contactGroup1";
            }

            return "";
        }

        return "group1Id";
    });

    protected readonly getGroup2Field = computed<string>(() => {
        const dashboard = this.dashboardsService.currentDashboard();
        const type = this.type();
        if (!dashboard || !type) return "";

        if (isJobsBillingDashboard(dashboard)) {
            if (type === CardPanelType.Job) {
                return "jobGroup2";
            }

            if (type === CardPanelType.Contact) {
                return "contactGroup2";
            }

            return "";
        }

        return "group2Id";
    });

    protected readonly canEditGroup = computed<GroupAccessFunction>(() => {
        const type = this.type();
        const canEdit = this.cardModalService.canContactsViewAddEdit();

        return (field: JobsJobAccessField | null = null): boolean => {
            if (!type) return true;
            if (type === CardPanelType.Job) {
                if (!field) return false;
                return this.jobsAndBillingCommonService.hasAccess(field);
            }

            return canEdit;
        };
    });

    protected readonly getGroup1Options = computed(() => {
        const group1 = this.iFirmGroupsService.group1()();
        if (!group1) return [];

        const selectedId = this.normalizeGroupId(this.currentGroup1Id());
        return group1
            .filter((group) => group.IsVisible || group.Id === selectedId)
            .sort((a, b) => {
                if (a.Id === -1 || a.Id === null) return -1;
                if (b.Id === -1 || b.Id === null) return 1;
                return (a[this.helpersService.getLang()] ?? "").localeCompare(b[this.helpersService.getLang()] ?? "");
            });
    });

    protected readonly getGroup2Options = computed(() => {
        const group2 = this.iFirmGroupsService.group2()();
        if (!group2) return [];

        const selectedId = this.normalizeGroupId(this.currentGroup2Id());
        return group2
            .filter((group) => group.IsVisible || group.Id === selectedId)
            .sort((a, b) => {
                if (a.Id === -1 || a.Id === null) return -1;
                if (b.Id === -1 || b.Id === null) return 1;
                return (a[this.helpersService.getLang()] ?? "").localeCompare(b[this.helpersService.getLang()] ?? "");
            });
    });

    private readonly currentGroup1Id = computed<string | null | undefined>(() => {
        const card = this.cardModalService.currentCard();
        if (!card) return undefined;
        const dashboard = this.dashboardsService.currentDashboard();
        if (dashboard?.ApplicationType === ApplicationType.JobsBilling) {
            switch (this.type()) {
                case CardPanelType.Job:
                    return (card as JobsBillingCard).job?.group1;
                case CardPanelType.Contact:
                    return (card as JobsBillingCard).contactInformation.group1;
                default:
                    return undefined;
            }
        } else {
            return (card as IBaseT1Card).group1Id;
        }
    });

    private readonly currentGroup2Id = computed<string | null | undefined>(() => {
        const card = this.cardModalService.currentCard();
        if (!card) return undefined;
        const dashboard = this.dashboardsService.currentDashboard();
        if (dashboard?.ApplicationType === ApplicationType.JobsBilling) {
            switch (this.type()) {
                case CardPanelType.Job:
                    return (card as JobsBillingCard).job?.group2;
                case CardPanelType.Contact:
                    return (card as JobsBillingCard).contactInformation.group2;
                default:
                    return undefined;
            }
        } else {
            return (card as IBaseT1Card).group2Id;
        }
    });

    private normalizeGroupId(groupId: string | null | undefined): number {
        if (groupId == null || groupId === "") return -1;
        const n = Number(groupId);
        return Number.isNaN(n) ? -1 : n;
    }

    protected getGroup1Caption() {
        return this.iFirmGroupsService.getGroup1Caption(this.helpersService.getLang());
    }

    protected getGroup2Caption() {
        return this.iFirmGroupsService.getGroup2Caption(this.helpersService.getLang());
    }
}
